import { useState } from "react";
import { FiInfo, FiX } from "react-icons/fi";
import type { LoadImpactSummary, DispatchSummary } from "../domain/types";
import { formatImpact, toSigFigs, formatScientific } from "../../../utils/format";
import { useRangeMode } from "../../impact/context/RangeModeContext";
import { RangeWhisker } from "../../impact/components/RangeVisuals";
import {
  isRangeReady,
  metricRange,
  type EndpointMetric,
  type ScenariosBlock,
} from "../../impact/domain/scenarios";

interface Props {
  portfolioName: string;
  summary: LoadImpactSummary;
  dispatch?: DispatchSummary | null;
  baCode?: string | null;
  scenarios?: ScenariosBlock;
}

const CATEGORY_INFO: Record<
  string,
  { title: string; unit: string; description: string }
> = {
  health: {
    title: "Human Health",
    unit: "Disability-Adjusted Life Years",
    description:
      "Disability-Adjusted Life Years — measures health impact from air pollution, " +
      "toxic emissions, and climate-related effects across the lifecycle. Net = " +
      "grid-supplied harm + on-site NG damages + RE infrastructure − RE benefits; " +
      "a negative net (red) means the load causes more harm than any on-site RE offsets.",
  },
  ecosystem: {
    title: "Ecosystem Quality",
    unit: "Potentially Disappeared Fraction of Species",
    description:
      "Measures biodiversity impact from acidification, ecotoxicity, eutrophication, " +
      "and land use across the lifecycle. Includes the marginal grid mix serving " +
      "residual load (air, soil & water — not carbon alone).",
  },
  economic: {
    title: "Economic Impact",
    unit: "USD",
    description:
      "Total monetized value of all environmental and health damages, using ReCiPe 2016 " +
      "endpoint damage cost factors. Includes health care costs, productivity losses, " +
      "ecosystem service losses, and resource depletion.",
  },
};

function CategoryTooltip({
  category,
}: {
  category: keyof typeof CATEGORY_INFO;
}) {
  const [open, setOpen] = useState(false);
  const info = CATEGORY_INFO[category];

  return (
    <span className="relative inline-block">
      <button
        onClick={() => setOpen(!open)}
        className="text-text-muted hover:text-text-secondary transition-colors ml-1 align-middle"
        aria-label={`About ${info.title}`}
      >
        <FiInfo size={12} />
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
          />
          <div className="absolute left-0 top-full mt-1 z-50 w-72 rounded-lg bg-surface-600 border border-border shadow-lg p-3">
            <div className="flex items-start justify-between mb-1">
              <span className="text-xs font-semibold text-text-primary">
                {info.title}
              </span>
              <button
                onClick={() => setOpen(false)}
                className="text-text-muted hover:text-text-secondary"
              >
                <FiX size={12} />
              </button>
            </div>
            <p className="text-[11px] leading-relaxed text-text-secondary">
              {info.description}
            </p>
          </div>
        </>
      )}
    </span>
  );
}

function formatUSD(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `$${(abs / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `$${(abs / 1_000).toFixed(1)}K`;
  return `$${abs.toFixed(2)}`;
}

export function LoadImpactSummaryCard({
  portfolioName,
  summary,
  dispatch,
  baCode,
  scenarios,
}: Props) {
  const { mode: rangeMode } = useRangeMode();
  const [lens, setLens] = useState<"consequential" | "attributional">("consequential");
  const hasAttr = !!summary.net_impacts_attributional;
  const attr = lens === "attributional" && hasAttr;
  // The sensitivity range is a consequential (marginal) concept — the
  // attributional lens shows a single average-mix value, so no whisker there.
  const showRange = rangeMode === "range" && isRangeReady(scenarios) && !attr;
  // Loads are harm-signed; the whisker is teal only when the net is a benefit.
  const loadWhisker = (
    metric: EndpointMetric,
    benefit: boolean,
    fmt: (n: number) => string,
  ) => {
    if (!showRange) return null;
    const r = metricRange(scenarios, "ctg", metric);
    if (!r) return null;
    return (
      <RangeWhisker
        floor={r.floor}
        central={r.central}
        ceiling={r.ceiling}
        benefit={benefit}
        format={fmt}
      />
    );
  };
  // Lens-dependent blocks: marginal = consequential, average = attributional.
  // ng_impacts + re_infrastructure are lens-invariant (identical under both).
  const net = attr ? summary.net_impacts_attributional! : summary.net_impacts;
  const gs = attr ? summary.grid_supply_attributional : summary.grid_supply;
  const reBenefits = attr ? summary.re_benefits_attributional! : summary.re_benefits;
  const hasGridSupply = !!gs && (gs.meta?.grid_import_mwh ?? 0) > 0;

  // net_impacts = re_benefits − (grid_supply + ng_impacts + re_infrastructure)
  // interpretation: positive = net benefit, negative = net harm
  const isNetBenefit = net.USD > 0;
  const isHealthHarm = net.DALYs < 0;
  const isEcoHarm = net.PDFs < 0;
  const isEconHarm = net.USD < 0;

  // Compute RE coverage from dispatch if available
  const reCoverage = dispatch?.re_coverage_pct;

  return (
    <div className="bg-surface-700 rounded-lg shadow border border-border overflow-hidden">
      {/* Title row */}
      <div className="px-6 pt-5 pb-1 flex items-center justify-between gap-3">
        <h2 className="text-lg font-medium text-text-primary">
          {portfolioName} — Environmental Impact Assessment
        </h2>
        {hasAttr && (
          <div className="inline-flex items-center bg-surface-600 rounded-md p-0.5 text-xs flex-shrink-0">
            {(
              [
                ["consequential", "Grid consequence"],
                ["attributional", "Average-grid footprint"],
              ] as const
            ).map(([v, label]) => (
              <button
                key={v}
                onClick={() => setLens(v)}
                title={
                  v === "consequential"
                    ? "Consequential (marginal): what changes on the grid if this load leaves — the decision basis."
                    : "Attributional (average mix): the average grid that served the load — the corporate / Scope-2 reporting basis."
                }
                className={`px-2.5 py-1 rounded font-medium transition-colors ${
                  lens === v
                    ? "bg-surface-500 text-text-primary shadow-sm"
                    : "text-text-muted hover:text-text-secondary"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>
      {/* Subtitle */}
      <div className="px-6 pb-3">
        <p className="text-sm text-text-secondary">
          {hasGridSupply
            ? "Net lifecycle impact: grid-supplied electricity + on-site generation, less RE benefits"
            : "Net impact of NG generation, offset by RE displacement benefits and RE infrastructure costs"}
          {reCoverage != null && (
            <> &middot; RE Coverage: {reCoverage.toFixed(1)}%</>
          )}
        </p>
        {hasAttr && (
          <p className="text-[11px] text-text-muted mt-1">
            {attr
              ? "Attributional basis: grid harm from the average generation mix that served this load (corporate / Scope-2 footprint)."
              : "Consequential basis: grid harm from the marginal units that respond to this load (what changes if it leaves)."}
          </p>
        )}
      </div>

      {/* 3-column impact breakdown */}
      <div className="grid grid-cols-3 gap-0 border-t border-border">
        {/* Health */}
        <div
          className={`border-l-4 p-4 ${
            isHealthHarm ? "border-l-red-400" : "border-l-health-400"
          }`}
        >
          <p
            className={`text-sm font-semibold uppercase tracking-wide mb-3 ${
              isHealthHarm ? "text-red-400" : "text-health-400"
            }`}
          >
            Human Health
            <CategoryTooltip category="health" />
            <span className="block font-normal normal-case tracking-normal text-text-muted mt-0.5">
              {CATEGORY_INFO.health.unit}
            </span>
          </p>

          {/* Net */}
          <p
            className={`text-2xl font-bold leading-tight ${
              isHealthHarm ? "text-red-400" : "text-health-400"
            }`}
          >
            {net.DALYs > 0 ? "+" : ""}
            {toSigFigs(net.DALYs)}
          </p>
          <p className="text-[11px] text-text-muted">
            {isHealthHarm ? "Net Harm" : "Net Benefit"} (Annual)
          </p>
          {loadWhisker("dalys", !isHealthHarm, (n) => toSigFigs(n))}

          {/* Breakdown */}
          <div className="mt-3 space-y-1 text-[11px]">
            {hasGridSupply && (
              <div className="flex justify-between">
                <span className="text-red-400/70">Grid Supply</span>
                <span className="text-text-secondary">
                  {toSigFigs(gs?.DALYs ?? 0)}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-red-400/70">NG Damages</span>
              <span className="text-text-secondary">
                {toSigFigs(summary.ng_impacts.DALYs)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-green-400/70">RE Benefits</span>
              <span className="text-text-secondary">
                {toSigFigs(reBenefits.DALYs)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-yellow-400/70">RE Infra</span>
              <span className="text-text-secondary">
                {toSigFigs(summary.re_infrastructure.DALYs)}
              </span>
            </div>
          </div>
        </div>

        {/* Ecosystem */}
        <div
          className={`border-l-4 p-4 ${
            isEcoHarm ? "border-l-red-400" : "border-l-ecosystem-400"
          }`}
        >
          <p
            className={`text-sm font-semibold uppercase tracking-wide mb-3 ${
              isEcoHarm ? "text-red-400" : "text-ecosystem-400"
            }`}
          >
            Ecosystem Quality
            <CategoryTooltip category="ecosystem" />
            <span className="block font-normal normal-case tracking-normal text-text-muted mt-0.5">
              {CATEGORY_INFO.ecosystem.unit}
            </span>
          </p>

          <p
            className={`text-2xl font-bold leading-tight ${
              isEcoHarm ? "text-red-400" : "text-ecosystem-400"
            }`}
          >
            {net.PDFs > 0 ? "+" : ""}
            {toSigFigs(net.PDFs)}
          </p>
          <p className="text-[11px] text-text-muted">
            {isEcoHarm ? "Net Harm" : "Net Benefit"} (Annual)
          </p>
          {loadWhisker("pdfs", !isEcoHarm, (n) => toSigFigs(n))}

          <div className="mt-3 space-y-1 text-[11px]">
            {hasGridSupply && (
              <div className="flex justify-between">
                <span className="text-red-400/70">Grid Supply</span>
                <span className="text-text-secondary">
                  {toSigFigs(gs?.PDFs ?? 0)}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-red-400/70">NG Damages</span>
              <span className="text-text-secondary">
                {toSigFigs(summary.ng_impacts.PDFs)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-green-400/70">RE Benefits</span>
              <span className="text-text-secondary">
                {toSigFigs(reBenefits.PDFs)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-yellow-400/70">RE Infra</span>
              <span className="text-text-secondary">
                {toSigFigs(summary.re_infrastructure.PDFs)}
              </span>
            </div>
          </div>
        </div>

        {/* Economic */}
        <div
          className={`border-l-4 p-4 ${
            isEconHarm ? "border-l-red-400" : "border-l-economic-400"
          }`}
        >
          <p
            className={`text-sm font-semibold uppercase tracking-wide mb-3 ${
              isEconHarm ? "text-red-400" : "text-economic-400"
            }`}
          >
            Economic Impact
            <CategoryTooltip category="economic" />
            <span className="block font-normal normal-case tracking-normal text-text-muted mt-0.5">
              {CATEGORY_INFO.economic.unit}
            </span>
          </p>

          <p
            className={`text-2xl font-bold leading-tight ${
              isEconHarm ? "text-red-400" : "text-economic-400"
            }`}
          >
            {net.USD > 0 ? "+" : "-"}
            {formatUSD(net.USD)}
          </p>
          <p className="text-[11px] text-text-muted">
            {isEconHarm ? "Net Harm" : "Net Benefit"} (Annual)
          </p>
          {loadWhisker("usd", !isEconHarm, (n) => formatUSD(n))}

          <div className="mt-3 space-y-1 text-[11px]">
            {hasGridSupply && (
              <div className="flex justify-between">
                <span className="text-red-400/70">Grid Supply</span>
                <span className="text-text-secondary">
                  {formatUSD(gs?.USD ?? 0)}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-red-400/70">NG Damages</span>
              <span className="text-text-secondary">
                {formatUSD(summary.ng_impacts.USD)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-green-400/70">RE Benefits</span>
              <span className="text-text-secondary">
                {formatUSD(reBenefits.USD)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-yellow-400/70">RE Infra</span>
              <span className="text-text-secondary">
                {formatUSD(summary.re_infrastructure.USD)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Warning banner when net harm */}
      {!isNetBenefit && (
        <div className="px-6 py-2 bg-red-500/5 border-t border-red-500/20">
          <p className="text-xs text-red-400/80">
            {hasGridSupply
              ? "This load carries a net environmental burden — the electricity it consumes causes more harm than any on-site RE offsets. More on-site/contracted renewables, or siting in a cleaner grid, reduces it."
              : "This load portfolio has a net environmental burden — NG generation damages outweigh RE displacement benefits. Adding more renewable capacity will reduce the net impact."}
          </p>
        </div>
      )}
    </div>
  );
}
